import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  NumberField,
  ReferenceField,
  TopToolbar,
  EditButton,
  ListButton,
  DeleteButton,
  useRecordContext,
} from 'react-admin';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Avatar,
  Chip,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  Tag as TagIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const ShowTopToolbar = () => (
  <TopToolbar>
    <EditButton />
    <DeleteButton
      confirmTitle='Eliminar Dashboard'
      confirmContent='¿Está seguro de que desea eliminar este dashboard? Esta acción no se puede deshacer.'
    />
    <ListButton />
  </TopToolbar>
);

const ShowTitle = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
        <DashboardIcon fontSize='large' />
      </Avatar>
      <Box>
        <Typography variant='h5' component='div' fontWeight='bold'>
          {record.dashboardName}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Dashboard ID: #{record.id}
        </Typography>
      </Box>
    </Box>
  );
};

const DashboardInfoField = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box>
      <Typography variant='body1' gutterBottom>
        <strong>Nombre:</strong> {record.dashboardName}
      </Typography>
      <Typography variant='body1' gutterBottom>
        <strong>Posición:</strong> {record.position || 'No definida'}
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
        Este dashboard forma parte del sistema de visualización de datos. La
        posición determina el orden de aparición en la interfaz.
      </Typography>
    </Box>
  );
};

// Campo personalizado para mostrar roles y permisos
export const RolePermissionsField = () => {
  const record = useRecordContext();

  if (!record || !record.rolePermissions) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {record.rolePermissions.length > 0 ? (
        record.rolePermissions.map((rp: any, index: number) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={rp.role}
              variant='outlined'
              color='primary'
              icon={<SecurityIcon />}
              sx={{
                backgroundColor: 'primary.50',
                borderColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.100',
                },
              }}
            />
            <Chip
              label={rp.permissionLevel}
              variant='filled'
              color='secondary'
              size='small'
              sx={{
                fontWeight: 'medium',
              }}
            />
          </Box>
        ))
      ) : (
        <Typography variant='body2' color='text.secondary' fontStyle='italic'>
          No hay roles asignados
        </Typography>
      )}
    </Box>
  );
};

const DashboardsShow = () => {
  return (
    <Show title='Detalles del Dashboard' actions={<ShowTopToolbar />}>
      <Box sx={{ margin: '0 auto', padding: 2 }}>
        <SimpleShowLayout>
          {/* Header con información principal */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
            <ShowTitle />
          </Paper>

          <Grid container spacing={3}>
            {/* Información del Dashboard */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: 340, boxShadow: 3 }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <InfoIcon />
                    </Avatar>
                  }
                  title={
                    <Typography variant='h6' component='div'>
                      Información del Dashboard
                    </Typography>
                  }
                  subheader='Datos básicos y configuración'
                />
                <Divider />
                <CardContent>
                  <DashboardInfoField />
                </CardContent>
              </Card>
            </Grid>

            {/* Grupo Asociado */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: 340, boxShadow: 3 }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      <GroupIcon />
                    </Avatar>
                  }
                  title={
                    <Typography variant='h6' component='div'>
                      Grupo de Dashboard
                    </Typography>
                  }
                  subheader='Grupo al que pertenece este dashboard'
                />
                <Divider />
                <CardContent>
                  <ReferenceField
                    source='dashboardGroupId'
                    reference='dashboardgroups'
                    link='show'
                  >
                    <Box>
                      <Typography variant='h6' color='primary' gutterBottom>
                        <TextField source='groupName' />
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Haz clic para ver los detalles del grupo de dashboard
                      </Typography>
                    </Box>
                  </ReferenceField>
                </CardContent>
              </Card>
            </Grid>

            {/* Roles y Permisos */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: 340, boxShadow: 3 }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                      <SecurityIcon />
                    </Avatar>
                  }
                  title={
                    <Typography variant='h6' component='div'>
                      Roles de Acceso
                    </Typography>
                  }
                  subheader='Roles que pueden acceder a este dashboard'
                />
                <Divider />
                <CardContent>
                  <RolePermissionsField />
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mt: 2 }}
                  >
                    Estos roles determinan qué usuarios pueden ver y gestionar
                    este dashboard.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Información del Sistema */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: 340, boxShadow: 3 }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                      <TagIcon />
                    </Avatar>
                  }
                  title={
                    <Typography variant='h6' component='div'>
                      Información del Sistema
                    </Typography>
                  }
                  subheader='Datos técnicos y metadatos'
                />
                <Divider />
                <CardContent>
                  <Box>
                    <Typography variant='body1' gutterBottom>
                      <strong>ID del Dashboard:</strong>{' '}
                      <TextField source='id' />
                    </Typography>
                    <Typography variant='body1' gutterBottom>
                      <strong>Posición:</strong>{' '}
                      <NumberField source='position' emptyText='No definida' />
                    </Typography>
                    <Typography variant='body1' gutterBottom>
                      <strong>ID del Grupo:</strong>{' '}
                      <NumberField source='dashboardGroupId' />
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mt: 2 }}
                    >
                      Información técnica utilizada por el sistema para la
                      gestión interna.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </SimpleShowLayout>
      </Box>
    </Show>
  );
};

export default DashboardsShow;
